import React from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import { themePropType, imageArrayPropType } from './sharedPropTypes';
import { motion, useMotionValue, useTransform } from "framer-motion";
import '../App.css';

const CustomModal = ({ isOpen, onRequestClose, theme, children }) => {
  // Motion values for mouse tracking
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  
  // Transform mouse position into rotation values
  const rotateX = useTransform(y, [-100, 100], [-5, 5]);
  const rotateY = useTransform(x, [-100, 100], [5, -5]);
  
  const handleMouseMove = (event) => {
    // Get mouse position relative to center of modal
    const bounds = event.currentTarget.getBoundingClientRect();
    const centerX = bounds.left + bounds.width / 2;
    const centerY = bounds.top + bounds.height / 2;
    
    x.set(event.clientX - centerX);
    y.set(event.clientY - centerY);
  };

  const handleMouseLeave = () => {
    // Reset to center when mouse leaves
    x.set(0);
    y.set(0);
  };

  // Close icons
  const closeIconDay = require('../static/images/xDay.webp');
  const closeIconDayH = require('../static/images/xDay-h.webp');
  const closeIconNight = require('../static/images/xNight.webp');
  const closeIconNightH = require('../static/images/xNight-h.webp');
  
  const closeIcon = theme === 'light' ? 
    { normal: closeIconDay, hover: closeIconDayH } : 
    { normal: closeIconNight, hover: closeIconNightH };
  
  const [isHoveringClose, setIsHoveringClose] = React.useState(false);
  const [isDesktop, setIsDesktop] = React.useState(false);

  // Check if desktop on mount and resize
  React.useEffect(() => {
    const checkIfDesktop = () => {
      setIsDesktop(window.innerWidth > 768); // Match your mobile breakpoint
    };
    
    checkIfDesktop();
    window.addEventListener('resize', checkIfDesktop);
    return () => window.removeEventListener('resize', checkIfDesktop);
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="modalClass"
      overlayClassName="modalOverlay"
      style={{
        content: { 
          border: `2px solid ${theme === 'light' ? '#614531' : '#dbecda'}`,
          backgroundColor: theme === 'light' ? '#ecd0eb' : '#5b539a',
          overflow: 'visible' // Important for 3D effects
        }
      }}
    >
      <motion.div
        className="modal3DContainer"
        onMouseMove={isDesktop ? handleMouseMove : undefined}
        onMouseLeave={isDesktop ? handleMouseLeave : undefined}
        style={{
          perspective: isDesktop ? '1000px' : 'none',
          width: '100%',
          height: '100%',
          position: 'relative'
        }}
      >
        <motion.div
          style={{
            width: '100%',
            height: '100%',
            rotateX: isDesktop ? rotateX : 0,
            rotateY: isDesktop ? rotateY : 0,
            transition: 'transform 0.1s ease-out',
            transformStyle: 'preserve-3d'
          }}
        >
          {/* Close button - now part of the 3D transform */}
          <motion.button 
            onClick={onRequestClose}
            className="modalCloseBtn"
            onMouseEnter={() => setIsHoveringClose(true)}
            onMouseLeave={() => setIsHoveringClose(false)}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              zIndex: 10,
              transform: 'translateZ(20px)' // Bring it slightly forward
            }}
          >
            <img 
              src={isHoveringClose ? closeIcon.hover : closeIcon.normal} 
              alt="Close modal"
              className='modalCloseIcon' 
              style={{ width: '30px', height: 'auto' }}
              draggable='false'
            />
          </motion.button>
          
          {/* Content */}
          <div className='modalContentWrapper'>
            {children}
          </div>
        </motion.div>
      </motion.div>
    </Modal>
  );
};

CustomModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    theme: themePropType,
    children: PropTypes.node,
    galleryImages: imageArrayPropType
};

export default CustomModal;